import React from 'react';
import { Typography, useTheme } from '@material-ui/core';
import { useTranslate } from 'react-admin';
import type { PortBoundary } from '../types/warehouse.types';
import { InfoBox } from './InfoBox';
import { InfoField } from './InfoField';
import { valueTextStyle } from './styles';
import { BuildingEntityBlockUnBlock } from './BuildingEntityBlockUnBlock';
import { formatDateTime } from '../utils/dateFormatters';

interface PortInfoProps {
  selectedPorts: PortBoundary[]; // All selected ports
  terminalId: number;
}

export const PortInfo: React.FC<PortInfoProps> = ({ selectedPorts, terminalId }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const textStyle = {
    margin: 0,
  };

  const dynamicTextStyle = valueTextStyle(theme);

  const portsToShow = selectedPorts;

  const hasBothButtons = portsToShow.length > 0 && 
    portsToShow.some(p => p.blocked) && 
    portsToShow.some(p => !p.blocked);

  return (
    <InfoBox 
      title={
        <>
          <span style={{ 
            flexShrink: 1, 
            minWidth: 0,
            flexBasis: hasBothButtons ? '100%' : 'auto'
          }}>
            {portsToShow.length > 1 ? translate('osfc.storage_ui.port_info.selected_ports') : translate('osfc.storage_ui.port_info.selected_port')}
          </span>
          {portsToShow.length > 0 && (
            <BuildingEntityBlockUnBlock
              selectedEntities={portsToShow}
              terminalId={terminalId}
              entityType="port"
              resource="ports"
              idField="id"
              displayField="name"
            />
          )}
        </>
      } 
      style={{ margin: '15px 0', maxHeight: '400px', overflowY: 'auto' }}
    >
      {portsToShow.length > 0 ? (
        <>
          {portsToShow.map((port, index) => (
            <div 
              key={port.name} 
              style={{ 
                marginBottom: portsToShow.length > 1 ? '15px' : '0', 
                paddingBottom: portsToShow.length > 1 && index < portsToShow.length - 1 ? '10px' : '0', 
                borderBottom: portsToShow.length > 1 && index < portsToShow.length - 1 
                  ? `1px solid ${theme.palette.storageUi.backgroundSecondary}` 
                  : 'none' 
              }}
            >
              {portsToShow.length > 1 && (
                <Typography
                  component="p"
                  variant="subtitle2"
                  style={{
                    ...textStyle,
                    fontWeight: 'bold',
                    marginBottom: '5px',
                    color: theme.palette.primary.main,
                  }}
                >
                  {translate('osfc.storage_ui.port_info.port_x_of_y', { index: index + 1, total: portsToShow.length })}
                </Typography>
              )}
              
              <InfoField
                label={translate('osfc.storage_ui.port_info.name')}
                value={port.name}
                valueStyle={dynamicTextStyle}
              />
              <InfoField
                label={translate('osfc.storage_ui.port_info.status')}
                value={port.blocked ? translate('osfc.storage_ui.port_info.blocked') : translate('osfc.storage_ui.port_info.empty')}
                valueStyle={{ ...dynamicTextStyle, color: port.blocked ? theme.palette.storageUi.errorText : theme.palette.storageUi.emptyTextColor }}
              />

              {port.blocked && (
                <>
                  <InfoField
                    label={translate('osfc.storage_ui.port_info.lock_from')}
                    value={formatDateTime(port.lock_datetime_from)}
                    valueStyle={dynamicTextStyle}
                  />
                  <InfoField
                    label={translate('osfc.storage_ui.port_info.lock_to')}
                    value={formatDateTime(port.lock_datetime_to)}
                    valueStyle={dynamicTextStyle}
                  />
                  {port.comment && (
                    <InfoField
                      label={translate('osfc.storage_ui.port_info.comment')}
                      value={port.comment}
                      valueStyle={dynamicTextStyle}
                    />
                  )}
                </>
              )}
            </div>
          ))}
        </>
      ) : (
        <Typography variant="body2" component="p" style={textStyle}>
          <span style={dynamicTextStyle}>
            {translate('osfc.storage_ui.port_info.click_port')}
          </span>
        </Typography>
      )}
    </InfoBox>
  );
};
